import { CircularProgress, Tooltip } from '@mui/material'
import { format } from 'date-fns'
import {
  Bike,
  Calendar,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Clock,
  Copy,
  CreditCard,
  Edit,
  FileText,
  MoreVertical,
  Package,
  PenTool,
  QrCode,
  RefreshCw,
  Scissors,
  Share,
  ShoppingCart,
  Trash2,
  Truck,
  Undo,
  User,
  Zap,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useOrdenation } from '../../hooks/useOrdenation'

const OrdersTable = ({
  orders,
  itemsPerPage,
  selectedOrders,
  handleOrderSelection,
  orderMoreAction,
  setOrderMoreAction,
  setShowModal,
  setPedido,
  setShowModalMr,
  getStatusBadgeVariant,
  getBadgeColorForSubstatus,
  statusMap,
  formatarDataDespacho,
  handleParcial,
  handleHistorico,
  handleRecadastrar,
  handleShopee,
  handleEstruturar,
  handleOpenModal,
  handleCloneOrder,
  copyLinkSignature,
  isOrderCopied,
  handleNfe,
  handleEstorno,
  loadingEstorno,
  handleDelete,
  handleExportOrderToBHDatabase,
  isExportOrderLoading,
  CustomSpinner,
}) => {
  // 🔹 Carregar Tailwind somente nesse componente
  const [isTailwindReady, setIsTailwindReady] = useState(false)

  const { filteredItems, Paginate, check, handleOrganize } = useOrdenation(
    orders,
    'DATA',
    'desc',
    Number(itemsPerPage),
  )

  useEffect(() => {
    const script = document.createElement('script')
    script.src = 'https://cdn.tailwindcss.com'
    script.async = true
    script.onload = () => setIsTailwindReady(true) // ✅ só libera quando carregar
    document.head.appendChild(script)

    return () => {
      document.head.removeChild(script)
    }
  }, [])

  const getIntegrationInfo = (integrado) => {
    switch (integrado) {
      case 'fulfillment':
        return {
          icon: Zap,
          text: 'Venda Full',
          color: 'bg-green-100 text-green-800',
        }
      case 'cross_docking':
        return {
          icon: Truck,
          text: 'Coleta',
          color: 'bg-blue-100 text-blue-800',
        }
      case 'self_service':
        return {
          icon: Bike,
          text: 'Envio Flex',
          color: 'bg-yellow-100 text-yellow-800',
        }
      case 'PEDIDO MANUAL':
        return {
          icon: Package,
          text: 'Manual',
          color: 'bg-gray-100 text-gray-800',
        }
      default:
        return {
          icon: Package,
          text: integrado,
          color: 'bg-gray-100 text-gray-800',
        }
    }
  }

  const getStatusColor = (status) => {
    const variant = getStatusBadgeVariant(status)
    const colorMap = {
      success: 'bg-green-100 text-green-800',
      warning: 'bg-yellow-100 text-yellow-800',
      danger: 'bg-red-100 text-red-800',
      primary: 'bg-blue-100 text-blue-800',
      secondary: 'bg-gray-100 text-gray-800',
    }
    return colorMap[variant] || 'bg-gray-100 text-gray-800'
  }

  const getTipoColor = (tipo) => {
    if (!tipo || tipo.toLowerCase() === 'venda')
      return 'bg-green-100 text-green-800'
    if (tipo.toLowerCase() === 'reposicao') return 'bg-blue-100 text-blue-800'
    if (tipo.toLowerCase() === 'condicional') return 'bg-red-100 text-red-800'
    return 'bg-gray-100 text-gray-800'
  }

  // 👉 aqui sim você pode condicionar o return
  if (!isTailwindReady) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <span className="text-gray-500 text-sm">Carregando estilos...</span>
      </div>
    )
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-full mx-auto p-4">
        {/* Header com ordenação */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-4 p-4">
          <div className="grid grid-cols-8 gap-4 text-sm font-medium text-gray-600">
            <div>Seleção</div>

            <div
              className="cursor-pointer hover:text-blue-600 flex items-center gap-2"
              onClick={() => handleOrganize('DATA')}
            >
              Data e Hora
              <div className="flex flex-col">
                <ChevronUp
                  size={12}
                  color={check('DATA', 'asc')}
                />
                <ChevronDown
                  size={12}
                  color={check('DATA', 'desc')}
                />
              </div>
            </div>
            <div
              className="cursor-pointer hover:text-blue-600 flex items-center gap-2"
              onClick={() => handleOrganize('CLIENTE')}
            >
              Cliente e Valor
              <div className="flex flex-col">
                <ChevronUp
                  size={12}
                  color={check('CLIENTE', 'asc')}
                />
                <ChevronDown
                  size={12}
                  color={check('CLIENTE', 'desc')}
                />
              </div>
            </div>
            <div
              className="cursor-pointer hover:text-blue-600 flex items-center gap-2"
              onClick={() => handleOrganize('INTEGRADO')}
            >
              Integração
              <div className="flex flex-col">
                <ChevronUp
                  size={12}
                  color={check('INTEGRADO', 'asc')}
                />
                <ChevronDown
                  size={12}
                  color={check('INTEGRADO', 'desc')}
                />
              </div>
            </div>
            <div>Status e Info</div>
            <div
              className="cursor-pointer hover:text-blue-600 flex items-center gap-2"
              onClick={() => handleOrganize('APELIDO')}
            >
              Plataforma
              <div className="flex flex-col">
                <ChevronUp
                  size={12}
                  color={check('APELIDO', 'asc')}
                />
                <ChevronDown
                  size={12}
                  color={check('APELIDO', 'desc')}
                />
              </div>
            </div>
            <div>Conferente</div>
            <div>Ações</div>
          </div>
        </div>

        {/* Orders List */}
        <div className="space-y-3">
          {filteredItems.map((order, index) => (
            <div
              key={Math.random()}
              className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200"
            >
              <div className="p-6">
                <div className="grid grid-cols-12 gap-4 items-start">
                  {/* Coluna 1: Checkbox + Status Financeiro */}
                  <div className="col-span-1 flex flex-col items-center gap-3">
                    <input
                      type="checkbox"
                      checked={selectedOrders.some(
                        (selectedOrder) =>
                          selectedOrder.PEDIDO === order.PEDIDO,
                      )}
                      onChange={() => handleOrderSelection(order)}
                      className="scale-110"
                    />

                    <Tooltip
                      title={
                        order.LANC_CONTAS === 'S'
                          ? 'Conta à Receber Lançada'
                          : 'Conta à Receber Não Lançada'
                      }
                      arrow
                    >
                      <div
                        className={`w-3 h-3 rounded-full ${order.LANC_CONTAS === 'S'
                          ? 'bg-green-400'
                          : 'bg-red-400'
                          }`}
                      />
                    </Tooltip>
                  </div>

                  {/* Coluna 2: Data e Hora */}
                  <div className="col-span-2">
                    <div className="bg-gray-50 px-3 py-2 rounded-lg border h-full">
                      <div className="flex items-center gap-2 text-gray-600 mb-1">
                        <Calendar size={14} />
                        <span className="text-xs font-medium">
                          {format(new Date(order.DATA), 'dd/MM/yyyy')}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-500">
                        <Clock size={14} />
                        <span className="text-xs">{order.HORA_IMPORTACAO}</span>
                      </div>
                    </div>
                  </div>

                  {/* Coluna 3: Cliente e Valor */}
                  <div className="col-span-3">
                    <div className="col-span-3 min-w-0">
                      <div className="bg-gray-50 px-3 py-2 rounded-lg border h-auto min-h-[72px] overflow-visible">
                        <p
                          className="font-medium text-gray-800 text-sm leading-snug mb-1 whitespace-normal break-all"
                          title={order.CLIENTE}
                        >
                          {order.CLIENTE}
                        </p>

                        {order.VALOR !== null ? (
                          <p className="text-xs text-green-600 font-medium leading-tight mb-2">
                            {parseFloat(order.VALOR).toLocaleString('pt-BR', {
                              style: 'currency',
                              currency: 'BRL',
                            })}
                          </p>
                        ) : (
                          <p className="text-xs text-gray-400 leading-tight mb-2">
                            Sem valor
                          </p>
                        )}

                        <div className="flex flex-wrap gap-1">
                          <span className="bg-orange-100 text-orange-800 text-[11px] px-2 py-0.5 rounded font-medium">
                            {order.PEDIDO}
                          </span>
                          <span className="bg-gray-100 text-gray-600 text-[11px] px-2 py-0.5 rounded">
                            {order.VENDEDOR}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Coluna 4: Integração */}
                  <div className="col-span-1 flex justify-center items-start">
                    {(() => {
                      const integration = getIntegrationInfo(order.INTEGRADO)
                      const IconComponent = integration.icon
                      return (
                        <span
                          className={`inline-flex items-center gap-1 px-3 py-2 rounded ${integration.color} text-xs whitespace-nowrap`}
                        >
                          <IconComponent size={12} />
                          {integration.text}
                        </span>
                      )
                    })()}
                  </div>

                  {/* Coluna 5: Status e Tipo */}
                  <div className="col-span-2">
                    <div className="space-y-2 h-full flex flex-col">
                      <span
                        className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.STATUS)} text-center justify-center`}
                      >
                        {order.STATUS}
                      </span>
                      {order.TIPO_PEDIDO && (
                        <span
                          className={`inline-flex px-2 py-1 rounded text-xs font-medium ${getTipoColor(order.TIPO_PEDIDO)} text-center justify-center`}
                        >
                          {order.TIPO_PEDIDO === 'PEDIDO MANUAL'
                            ? 'Manual'
                            : order.TIPO_PEDIDO}
                        </span>
                      )}

                      <div
                        className={`inline-flex px-2 py-1 rounded text-xs ${order.IMPRESSO === 'S' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'} text-center justify-center`}
                      >
                        {order.IMPRESSO === 'S' ? 'Impresso' : 'Não impresso'}
                      </div>
                    </div>
                  </div>

                  {/* Coluna 6: Plataforma e IDs */}
                  <div className="col-span-1">
                    <div className="space-y-2 h-full flex flex-col items-center">
                      <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded w-full text-center">
                        {order.APELIDO}
                      </span>
                      <div
                        className="text-xs text-blue-600 cursor-pointer hover:underline text-center bg-blue-50 py-1 px-2 rounded w-full"
                        onClick={() =>
                          window.open(
                            `https://ziphub.com.br/pedido/${order.IDPEDIDOLOJA}/${process.env.REACT_APP_DATABASE_MATRIZ}`,
                            '_blank',
                          )
                        }
                      >
                        {order.IDPEDIDOLOJA}
                      </div>
                      <span className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded w-full text-center">
                        {order.SHIP}
                      </span>
                    </div>
                  </div>

                  {/* Coluna 7: Conferente */}
                  <div className="col-span-1">
                    <div className="h-full flex flex-col items-center justify-start">
                      {order.CONFERENTE ? (
                        <div className="space-y-1 text-center">
                          <div className="flex items-center justify-center gap-1 bg-gray-100 px-2 py-1 rounded text-xs">
                            <User size={12} />
                            <span>{order.CONFERENTE}</span>
                          </div>
                          {order.DATA_CONFERENCIA && (
                            <div className="text-xs text-gray-500">
                              {format(
                                new Date(order.DATA_CONFERENCIA),
                                'dd/MM/yyyy HH:mm',
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">
                          Não atribuído
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Coluna 8: Ações */}
                  <div className="col-span-1 relative">
                    {/* Botões principais sempre visíveis */}
                    <div className="flex gap-1 mb-2">
                      <Tooltip title="Editar">
                        <button
                          className="p-1.5 rounded border hover:bg-gray-50 w-7 h-7 flex items-center justify-center"
                          onClick={() =>
                            window.open(
                              `/pedido/editar/${order.PEDIDO}`,
                              '_blank',
                            )
                          }
                        >
                          <Edit size={12} />
                        </button>
                      </Tooltip>

                      <Tooltip title="Ver pedido">
                        <Link
                          to={`/pedido/${order.PEDIDO}/${localStorage.idconta}`}
                          className="p-1.5 rounded border hover:bg-gray-50 w-7 h-7 flex items-center justify-center"
                        >
                          <ShoppingCart size={12} />
                        </Link>
                      </Tooltip>

                      <Tooltip title="Ir para NFe">
                        <button
                          onClick={() => {
                            handleNfe(order.ID_LOJA, order.ID_NFE)
                            setOrderMoreAction({ [index]: false })
                          }}
                          className="p-1.5 rounded border hover:bg-gray-50 w-7 h-7 flex items-center justify-center"
                        >
                          <FileText size={12} />
                        </button>
                      </Tooltip>
                    </div>

                    {/* Botão de Mais Opções */}
                    <div className="flex justify-center">
                      <button
                        onClick={() =>
                          setOrderMoreAction((prevState) =>
                            prevState[index]
                              ? { [index]: false }
                              : { [index]: true },
                          )
                        }
                        className="p-2 rounded-full border hover:bg-gray-50 w-8 h-8 flex items-center justify-center bg-white shadow-sm hover:shadow-md transition-all duration-200"
                      >
                        <MoreVertical size={14} />
                      </button>
                    </div>

                    {/* Dropdown Menu */}
                    {orderMoreAction[index] && (
                      <>
                        {/* Menu Dropdown */}
                        <div
                          className="absolute right-0 top-12 z-50 bg-white rounded-xl shadow-2xl border border-gray-200 py-2 min-w-[240px] max-h-96 overflow-y-auto animate-in fade-in-0 zoom-in-95 duration-200"
                          role="menu"
                          aria-label="Menu de ações do pedido"
                        >
                          {/* Ação rápida: Recebimentos */}
                          <div className="px-4 py-2">
                            <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                              Ação Rápida
                            </h5>
                            <button
                              onClick={() => {
                                setShowModalMr(true)
                                setPedido(order.PEDIDO)
                                setOrderMoreAction({ [index]: false })
                              }}
                              className="w-full px-3 py-3 text-left text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-600 flex items-center gap-3 transition-all duration-200 rounded-lg border border-transparent hover:border-orange-200"
                              role="menuitem"
                            >
                              <CreditCard
                                size={16}
                                className="text-orange-500"
                              />
                              <div>
                                <div className="font-medium">Recebimentos</div>
                                <div className="text-xs text-gray-400">
                                  Gerenciar pagamentos do pedido
                                </div>
                              </div>
                            </button>

                            <button
                              onClick={() => {
                                setShowModal(true)
                                setPedido(order.PEDIDO)
                                setOrderMoreAction({ [index]: false })
                              }}
                              className="w-full px-3 py-3 text-left text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 flex items-center gap-3 transition-all duration-200 rounded-lg border border-transparent hover:border-blue-200 mt-2"
                              role="menuitem"
                            >
                              <PenTool
                                size={16}
                                className="text-blue-500"
                              />
                              <div>
                                <div className="font-medium">
                                  Alterar Status
                                </div>
                                <div className="text-xs text-gray-400">
                                  Mudar status do pedido
                                </div>
                              </div>
                            </button>
                          </div>

                          <div className="border-t border-gray-100 my-1" />

                          {/* Seção: Gestão do Pedido */}
                          <div className="px-4 py-2">
                            <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                              Gestão do Pedido
                            </h5>

                            <div className="space-y-1">
                              <button
                                onClick={() => {
                                  handleOpenModal(order)
                                  setOrderMoreAction({ [index]: false })
                                }}
                                className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-teal-50 hover:text-teal-600 flex items-center gap-3 transition-all duration-200 rounded-lg"
                                role="menuitem"
                              >
                                <Package
                                  size={16}
                                  className="text-teal-500"
                                />
                                <span>Ver Itens</span>
                              </button>

                              <button
                                onClick={() => {
                                  handleHistorico(order.PEDIDO)
                                  setOrderMoreAction({ [index]: false })
                                }}
                                className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 flex items-center gap-3 transition-all duration-200 rounded-lg"
                                role="menuitem"
                              >
                                <FileText
                                  size={16}
                                  className="text-blue-500"
                                />
                                <span>Histórico</span>
                              </button>

                              <button
                                onClick={() => {
                                  handleCloneOrder(order)
                                  setOrderMoreAction({ [index]: false })
                                }}
                                className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-pink-50 hover:text-pink-600 flex items-center gap-3 transition-all duration-200 rounded-lg"
                                role="menuitem"
                              >
                                <Copy
                                  size={16}
                                  className="text-pink-500"
                                />
                                <span>Clonar Pedido</span>
                              </button>
                            </div>
                          </div>

                          <div className="border-t border-gray-100 my-1" />

                          {/* Seção: Operações Especiais */}
                          <div className="px-4 py-2">
                            <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                              Operações
                            </h5>

                            <div className="space-y-1">
                              <button
                                onClick={() => {
                                  handleParcial(order.PEDIDO)
                                  setOrderMoreAction({ [index]: false })
                                }}
                                className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-600 flex items-center gap-3 transition-all duration-200 rounded-lg"
                                role="menuitem"
                              >
                                <Package
                                  size={16}
                                  className="text-orange-500"
                                />
                                <span>Estorno Parcial</span>
                              </button>

                              <button
                                onClick={() => {
                                  handleEstorno(order.PEDIDO)
                                  setOrderMoreAction({ [index]: false })
                                }}
                                disabled={loadingEstorno[order.PEDIDO]}
                                className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 flex items-center gap-3 transition-all duration-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                                role="menuitem"
                              >
                                {loadingEstorno[order.PEDIDO] ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <Undo
                                    size={16}
                                    className="text-blue-500"
                                  />
                                )}
                                <span>Estornar Estoque</span>
                              </button>

                              <button
                                onClick={() => {
                                  handleEstruturar(order.PEDIDO)
                                  setOrderMoreAction({ [index]: false })
                                }}
                                className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-600 flex items-center gap-3 transition-all duration-200 rounded-lg"
                                role="menuitem"
                              >
                                <RefreshCw
                                  size={16}
                                  className="text-indigo-500"
                                />
                                <span>Reestruturar</span>
                              </button>
                            </div>
                          </div>

                          <div className="border-t border-gray-100 my-1" />

                          {/* Seção: Integrações */}
                          <div className="px-4 py-2">
                            <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                              Integrações
                            </h5>

                            <div className="space-y-1">
                              <button
                                onClick={() => {
                                  handleRecadastrar(order.PEDIDO)
                                  setOrderMoreAction({ [index]: false })
                                }}
                                className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-green-50 hover:text-green-600 flex items-center gap-3 transition-all duration-200 rounded-lg"
                                role="menuitem"
                              >
                                <User
                                  size={16}
                                  className="text-green-500"
                                />
                                <span>Recadastrar Cliente</span>
                              </button>

                              <button
                                onClick={() => {
                                  handleShopee(
                                    order.IDPEDIDOLOJA,
                                    order.ID_LOJA,
                                    order.SHIP,
                                  )
                                  setOrderMoreAction({ [index]: false })
                                }}
                                className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-600 flex items-center gap-3 transition-all duration-200 rounded-lg"
                                role="menuitem"
                              >
                                <QrCode
                                  size={16}
                                  className="text-purple-500"
                                />
                                <span>Organizar Shopee</span>
                              </button>

                              <button
                                onClick={() => {
                                  handleNfe(order.ID_LOJA, order.ID_NFE)
                                  setOrderMoreAction({ [index]: false })
                                }}
                                className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 flex items-center gap-3 transition-all duration-200 rounded-lg"
                                role="menuitem"
                              >
                                <FileText
                                  size={16}
                                  className="text-blue-500"
                                />
                                <span>Ir para NFE</span>
                              </button>
                            </div>
                          </div>

                          <div className="border-t border-gray-100 my-1" />

                          {/* Seção: Ferramentas */}
                          <div className="px-4 py-2">
                            <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                              Ferramentas
                            </h5>

                            <div className="space-y-1">
                              <button
                                onClick={() => {
                                  copyLinkSignature(order.PEDIDO)
                                  setOrderMoreAction({ [index]: false })
                                }}
                                className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-yellow-50 hover:text-yellow-600 flex items-center gap-3 transition-all duration-200 rounded-lg"
                                role="menuitem"
                              >
                                {isOrderCopied[order.PEDIDO] ? (
                                  <CheckCircle
                                    size={16}
                                    className="text-green-500"
                                  />
                                ) : (
                                  <Scissors
                                    size={16}
                                    className="text-yellow-500"
                                  />
                                )}
                                <span>
                                  {isOrderCopied[order.PEDIDO]
                                    ? 'Link Copiado!'
                                    : 'Copiar Link Assinatura'}
                                </span>
                              </button>

                              {localStorage.idconta === '111111111' && (
                                <button
                                  onClick={() => {
                                    handleExportOrderToBHDatabase(
                                      order.PEDIDO,
                                      index,
                                    )
                                    setOrderMoreAction({ [index]: false })
                                  }}
                                  className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-600 flex items-center gap-3 transition-all duration-200 rounded-lg"
                                  role="menuitem"
                                >
                                  {isExportOrderLoading[index] ? (
                                    <CustomSpinner
                                      size={16}
                                      color="#666"
                                    />
                                  ) : (
                                    <Share
                                      size={16}
                                      className="text-gray-500"
                                    />
                                  )}
                                  <span>Exportar para Matriz</span>
                                </button>
                              )}
                            </div>
                          </div>

                          <div className="border-t border-gray-100 my-1" />

                          {/* Seção: Ações Críticas */}
                          <div className="px-4 py-2">
                            <h5 className="text-xs font-semibold text-red-500 uppercase tracking-wide mb-2">
                              ⚠️ Zona de Perigo
                            </h5>

                            <button
                              onClick={() => {
                                handleDelete(order.PEDIDO)
                                setOrderMoreAction({ [index]: false })
                              }}
                              className="w-full px-3 py-2 text-left text-sm text-red-700 hover:bg-red-50 hover:text-red-600 flex items-center gap-3 transition-all duration-200 rounded-lg border border-red-200"
                              role="menuitem"
                            >
                              <Trash2
                                size={16}
                                className="text-red-500"
                              />
                              <span>Deletar Pedido</span>
                            </button>
                          </div>
                        </div>

                        {/* Backdrop para fechar o menu */}
                        <div
                          className="fixed inset-0 z-40"
                          onClick={() => setOrderMoreAction({ [index]: false })}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                              setOrderMoreAction({ [index]: false })
                            }
                          }}
                          aria-label="Fechar menu"
                        />
                      </>
                    )}
                  </div>
                </div>

                {/* Rodapé extra */}
                {(order.DATADESPACHO ||
                  order.BUFFERING_DATE ||
                  order.SHIP_SUBSTATUS) && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <div className="flex flex-wrap gap-2 text-xs">
                        {order.SHIP_SUBSTATUS && (
                          <span
                            className={`px-2 py-1 rounded ${getBadgeColorForSubstatus(order.SHIP_SUBSTATUS)}`}
                          >
                            {statusMap[order.SHIP_SUBSTATUS] ??
                              order.SHIP_SUBSTATUS}
                          </span>
                        )}
                        {order.SHIP_DESPACHO && (
                          <span
                            className={`px-2 py-1 rounded ${getBadgeColorForSubstatus(order.SHIP_DESPACHO)}`}
                          >
                            {statusMap[order.SHIP_DESPACHO] ??
                              order.SHIP_DESPACHO}
                          </span>
                        )}
                        {order.DATADESPACHO && (
                          <span className="px-2 py-1 rounded bg-gray-100 text-gray-700">
                            Despachar {formatarDataDespacho(order.DATADESPACHO)}
                          </span>
                        )}
                        {order.BUFFERING_DATE && (
                          <span className="px-2 py-1 rounded bg-red-100 text-red-700">
                            Etiqueta retida até:{' '}
                            {formatarDataDespacho(order.BUFFERING_DATE)}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
              </div>
            </div>
          ))}
        </div>
        {/* Paginação */}
        <div className="flex justify-center mt-6">
          <Paginate />
        </div>
      </div>
    </div>
  )
}

export default OrdersTable
